import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import ResearchShell from '../components/research/ResearchShell'
import MacroConditionsPanel from '../components/research/MacroConditionsPanel'
import { SectorChips, SignedBar, StatTile } from '../components/research/charts'
import type { MarkerGroup, MarkerLabResult, Sector } from '../types/research'
import { analyzeMarkerInfluence, isResearchUsingMockData } from '../lib/research-api'

const GROUP_META: Record<MarkerGroup, { label: string; dot: string }> = {
  options: { label: 'Options chain', dot: 'bg-blue-500' },
  sentiment: { label: 'X & news sentiment', dot: 'bg-violet-500' },
  fundamental: { label: 'Earnings & statements', dot: 'bg-emerald-500' },
  macro: { label: 'Macro (Fed, jobs, forex)', dot: 'bg-amber-500' },
  factor: { label: 'Fama–French factors', dot: 'bg-rose-500' },
}

function card(extra = '') {
  return `bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-4 ${extra}`
}

function fmtMcapB(v: number): string {
  return v >= 1000 ? `$${(v / 1000).toFixed(2)}T` : `$${v.toFixed(0)}B`
}

export default function MarkerLabPage() {
  const navigate = useNavigate()
  const [sector, setSector] = useState<Sector | null>(null)
  const [horizon, setHorizon] = useState(4)
  const [result, setResult] = useState<MarkerLabResult | null>(null)
  const [loading, setLoading] = useState(false)
  const [isMock, setIsMock] = useState(false)
  const [sizeSector, setSizeSector] = useState<Sector | null>(null)

  useEffect(() => {
    let cancelled = false
    async function run() {
      setLoading(true)
      try {
        const r = await analyzeMarkerInfluence({ sector, horizonWeeks: horizon })
        if (cancelled) return
        setResult(r)
        setIsMock(isResearchUsingMockData())
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    run()
    return () => {
      cancelled = true
    }
  }, [sector, horizon])

  const maxAbsCorr = useMemo(
    () => Math.max(...(result?.influences.map((i) => Math.abs(i.correlation)) ?? [0]), 1e-6),
    [result]
  )

  const sizeSectors = useMemo(() => {
    const s = new Set<Sector>()
    result?.sizePlacements.forEach((p) => s.add(p.sector))
    return [...s]
  }, [result])
  const activeSizeSector = sector ?? sizeSector ?? sizeSectors[0] ?? null
  const sizeRows = result?.sizePlacements.filter((p) => p.sector === activeSizeSector) ?? []

  return (
    <ResearchShell showMockBanner={isMock && !loading}>
      {/* Controls */}
      <div className={card('mb-6')}>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-slate-900 dark:text-white">
            Unsupervised marker-influence analysis
          </h2>
          <label className="flex items-center gap-2">
            <span className="text-xs text-slate-500 dark:text-slate-400">Forward horizon</span>
            <select
              value={horizon}
              onChange={(e) => setHorizon(Number(e.target.value))}
              className="bg-slate-100 dark:bg-slate-900 text-slate-900 dark:text-white text-sm rounded-lg px-2.5 py-1.5 border border-slate-300 dark:border-slate-600 focus:border-blue-500 outline-none"
            >
              <option value={2}>2 weeks</option>
              <option value={4}>4 weeks</option>
              <option value={8}>8 weeks</option>
              <option value={12}>12 weeks</option>
            </select>
          </label>
        </div>
        <div className="mb-3">
          <SectorChips value={sector} onChange={setSector} />
        </div>
        <p className="text-xs text-slate-500 dark:text-slate-400">
          Pipeline: weekly markers are standardized, PCA on the marker correlation matrix extracts the latent
          factors (unsupervised), and each lagged marker is ranked by its correlation with {horizon}-week forward
          returns. Fama–French loadings come from an OLS of sector returns on the Mkt/SMB/HML factor series.
        </p>
      </div>

      {/* Macro backdrop: Fed, jobs, and the dollar — the forces the macro markers test */}
      <div className="mb-6">
        <MacroConditionsPanel />
      </div>

      {loading && (
        <div className={card('animate-pulse')}>
          {[1, 2, 3, 4, 5, 6, 7].map((i) => (
            <div key={i} className="h-9 bg-slate-200 dark:bg-slate-700 rounded mb-2" />
          ))}
        </div>
      )}

      {!loading && result && (
        <div className="space-y-6">
          {/* Sample summary */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatTile label="Scope" value={result.scope} sub={`${result.symbolsAnalyzed} equities`} />
            <StatTile label="Sample" value={`${result.sampleWeeks} weeks`} sub={`${result.observations.toLocaleString()} observations`} />
            <StatTile
              label="Top marker"
              value={result.influences[0]?.label.split(':')[0] ?? '—'}
              sub={result.influences[0] ? `${result.influences[0].influence}% of total influence` : undefined}
              tone="accent"
            />
            <StatTile
              label="PC1 variance explained"
              value={`${((result.components[0]?.varianceExplained ?? 0) * 100).toFixed(1)}%`}
              sub={result.components[0]?.interpretation}
            />
          </div>

          {/* Influence ranking */}
          <div className={card()}>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
              <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
                Marker influence on {result.horizonWeeks}-week forward returns
              </h3>
              <div className="flex flex-wrap gap-3">
                {(Object.keys(GROUP_META) as MarkerGroup[]).map((g) => (
                  <span key={g} className="flex items-center gap-1.5 text-[11px] text-slate-500 dark:text-slate-400">
                    <span className={`w-2 h-2 rounded-full ${GROUP_META[g].dot}`} />
                    {GROUP_META[g].label}
                  </span>
                ))}
              </div>
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
              Bar = correlation with forward return (green right = positive, red left = negative). Influence % is
              each marker's share of total absolute correlation.
            </p>
            <div className="space-y-2">
              {result.influences.map((inf) => (
                <div key={inf.marker} className="grid grid-cols-[minmax(0,15rem)_1fr_auto] gap-3 items-center">
                  <span className="flex items-center gap-2 min-w-0">
                    <span className={`w-2 h-2 rounded-full shrink-0 ${GROUP_META[inf.group].dot}`} />
                    <span className="text-xs text-slate-700 dark:text-slate-200 truncate" title={inf.label}>
                      {inf.label}
                    </span>
                  </span>
                  <SignedBar value={inf.correlation} maxAbs={maxAbsCorr} />
                  <span className="text-right whitespace-nowrap">
                    <span className="text-xs font-semibold text-slate-900 dark:text-white">{inf.influence}%</span>
                    <span className="text-[11px] text-slate-500 dark:text-slate-400 ml-2">
                      r={inf.correlation >= 0 ? '+' : ''}
                      {inf.correlation.toFixed(3)} · t={inf.tStat}
                    </span>
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* PCA latent factors */}
          <div className={card()}>
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-1">
              Latent factors (PCA on the marker correlation matrix)
            </h3>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
              Principal components of the marker panel — the unsupervised structure the markers move on, before any
              return data is consulted.
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              {result.components.map((c) => (
                <div key={c.component} className="bg-slate-100/70 dark:bg-slate-900/50 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-semibold text-slate-900 dark:text-white">
                      PC{c.component} · {c.interpretation}
                    </span>
                    <span className="text-[11px] text-slate-500 dark:text-slate-400">
                      {(c.varianceExplained * 100).toFixed(1)}% var · {(c.cumulativeVariance * 100).toFixed(0)}% cum
                    </span>
                  </div>
                  <div className="h-1.5 rounded bg-slate-200 dark:bg-slate-700 mb-3 overflow-hidden">
                    <div className="h-full bg-blue-500/80 rounded" style={{ width: `${c.varianceExplained * 100}%` }} />
                  </div>
                  <div className="space-y-1.5">
                    {c.topLoadings.map((l) => (
                      <div key={l.marker} className="grid grid-cols-[minmax(0,11rem)_1fr_auto] gap-2 items-center">
                        <span className="text-[11px] text-slate-600 dark:text-slate-300 truncate" title={l.label}>
                          {l.label}
                        </span>
                        <SignedBar value={l.loading} maxAbs={1} positiveCls="bg-blue-500/80" negativeCls="bg-amber-500/80" />
                        <span className="text-[11px] text-slate-500 dark:text-slate-400 w-12 text-right">
                          {l.loading >= 0 ? '+' : ''}
                          {l.loading.toFixed(2)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Fama-French per sector */}
          <div className={card()}>
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-3">
              Fama–French loadings by sector
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-slate-500 border-b border-slate-200 dark:border-slate-700">
                    <th className="py-2 pr-3 font-medium">Sector</th>
                    <th className="py-2 pr-3 font-medium text-right" title="Beta to the market factor">β Market</th>
                    <th className="py-2 pr-3 font-medium text-right" title="Small-minus-big size loading">SMB</th>
                    <th className="py-2 pr-3 font-medium text-right" title="High-minus-low value loading">HML</th>
                    <th className="py-2 pr-3 font-medium text-right" title="Annualized intercept">α / yr</th>
                    <th className="py-2 font-medium text-right">R²</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200/70 dark:divide-slate-700/50">
                  {result.sectorFactors.map((f) => (
                    <tr key={f.sector}>
                      <td className="py-2 pr-3 text-slate-700 dark:text-slate-200">{f.sector}</td>
                      <td className="py-2 pr-3 text-right text-slate-600 dark:text-slate-300">{f.marketBeta.toFixed(2)}</td>
                      <td className={`py-2 pr-3 text-right font-medium ${f.smb >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                        {f.smb >= 0 ? '+' : ''}
                        {f.smb.toFixed(2)}
                      </td>
                      <td className={`py-2 pr-3 text-right font-medium ${f.hml >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                        {f.hml >= 0 ? '+' : ''}
                        {f.hml.toFixed(2)}
                      </td>
                      <td className="py-2 pr-3 text-right text-slate-600 dark:text-slate-300">{f.alphaAnnualPct}%</td>
                      <td className="py-2 text-right text-slate-500 dark:text-slate-400">{f.r2.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Size within sector */}
          <div className={card()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Size placement within sector</h3>
              {!sector && sizeSectors.length > 1 && (
                <select
                  value={activeSizeSector ?? ''}
                  onChange={(e) => setSizeSector(e.target.value as Sector)}
                  className="bg-slate-100 dark:bg-slate-900 text-slate-900 dark:text-white text-xs rounded-lg px-2 py-1.5 border border-slate-300 dark:border-slate-600 focus:border-blue-500 outline-none"
                >
                  {sizeSectors.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div className="space-y-2">
              {sizeRows.map((p) => (
                <div key={p.symbol} className="grid grid-cols-[minmax(0,12rem)_1fr_auto] gap-3 items-center">
                  <span className="min-w-0">
                    <Link
                      to={`/dashboard/financial-app/fundamentals/${p.symbol}`}
                      className="text-xs font-semibold text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      {p.symbol}
                    </Link>
                    <span className="block text-[11px] text-slate-500 truncate">{p.name}</span>
                  </span>
                  <div className="h-3.5 rounded bg-slate-200/60 dark:bg-slate-700/40 overflow-hidden">
                    <div className="h-full rounded bg-emerald-500/70" style={{ width: `${Math.max(3, p.percentile)}%` }} />
                  </div>
                  <span className="text-right whitespace-nowrap text-[11px] text-slate-500 dark:text-slate-400">
                    <span className="text-xs font-medium text-slate-900 dark:text-white mr-2">{fmtMcapB(p.marketCap)}</span>
                    #{p.rankInSector}/{p.sectorCount} · P{p.percentile} ·{' '}
                    <span
                      className={`px-1.5 py-0.5 rounded-full font-medium ${
                        p.bucket === 'Mega'
                          ? 'bg-indigo-500/15 text-indigo-600 dark:text-indigo-300'
                          : p.bucket === 'Large'
                            ? 'bg-blue-500/15 text-blue-600 dark:text-blue-300'
                            : p.bucket === 'Mid'
                              ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-300'
                              : 'bg-amber-500/15 text-amber-600 dark:text-amber-300'
                      }`}
                    >
                      {p.bucket}
                    </span>
                  </span>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-3">
              Lower percentile = smaller within the sector → higher expected SMB (size-factor) exposure.
            </p>
          </div>

          {/* Composite signals */}
          <div className={card()}>
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-1">
              Composite marker signal by name
            </h3>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
              Current marker readings weighted by each marker's measured influence. Top 15 shown.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-slate-500 border-b border-slate-200 dark:border-slate-700">
                    <th className="py-2 pr-3 font-medium">Symbol</th>
                    <th className="py-2 pr-3 font-medium hidden md:table-cell">Sector</th>
                    <th className="py-2 pr-3 font-medium text-right">Signal (σ)</th>
                    <th className="py-2 pr-3 font-medium text-right">E[return] / yr</th>
                    <th className="py-2 pr-3 font-medium text-right hidden sm:table-cell">Vol / yr</th>
                    <th className="py-2 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200/70 dark:divide-slate-700/50">
                  {result.symbolSignals.slice(0, 15).map((s) => (
                    <tr key={s.symbol} className="hover:bg-slate-100/70 dark:hover:bg-slate-700/20 transition-colors">
                      <td className="py-2 pr-3">
                        <Link
                          to={`/dashboard/financial-app/fundamentals/${s.symbol}`}
                          className="font-semibold text-blue-600 dark:text-blue-400 hover:underline"
                        >
                          {s.symbol}
                        </Link>
                        <span className="block text-xs text-slate-500 max-w-[160px] truncate">{s.name}</span>
                      </td>
                      <td className="py-2 pr-3 text-slate-500 dark:text-slate-400 hidden md:table-cell">{s.sector}</td>
                      <td className={`py-2 pr-3 text-right font-semibold ${s.signalZ >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                        {s.signalZ >= 0 ? '+' : ''}
                        {s.signalZ.toFixed(2)}
                      </td>
                      <td className="py-2 pr-3 text-right text-slate-600 dark:text-slate-300">{s.expectedAnnualReturnPct}%</td>
                      <td className="py-2 pr-3 text-right text-slate-500 dark:text-slate-400 hidden sm:table-cell">{s.annualVolPct}%</td>
                      <td className="py-2 text-right whitespace-nowrap">
                        <Link
                          to={`/dashboard/financial-app/options/${s.symbol}`}
                          className="text-xs text-blue-600 dark:text-blue-400 hover:underline mr-3"
                        >
                          Options
                        </Link>
                        <button
                          onClick={() => navigate(`/dashboard/financial-app/research?task=analyze-security&symbol=${s.symbol}`)}
                          className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
                        >
                          Ask Opus
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <p className="text-[11px] text-slate-400 dark:text-slate-500">
            Research analytics for educational use — historical marker relationships are not guarantees of future
            prices, and nothing here is investment advice.
          </p>
        </div>
      )}
    </ResearchShell>
  )
}
